import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, parseApiError, type MatchCardGameResult } from '../lib/api';
import { CDL_2026_MODES, getModeShortLabel } from '../lib/modes';

// ─────────────────────────────────────────────────────────────────────────────
// ResultEntryForm — R1 (comp-lifecycle-pipeline) manual result entry, the
// no-OCR scoring path (POST /match/:matchId/result). Extracted from
// AdminPanel.tsx's MatchRow (its original home) so R2's seeding-rounds panel
// (StagePipelinePanel.tsx's SeedingRoundsPanel) can reuse it INLINE for a
// stage-generated match instead of deep-linking out to Admin > Matches —
// the component had no AdminPanel-local state dependency, so this was a
// clean lift, not a rewrite. AdminPanel.tsx's MatchRow now imports this same
// component; behavior is unchanged there.
// ─────────────────────────────────────────────────────────────────────────────

// Fallback used only when a match row somehow has no resolvedSeries (older
// cached query data mid-refetch) — mirrors the backend's own BO3 anchor
// (resolveMatchSeries's legacy tier) so the form never renders empty.
const FALLBACK_SERIES = { length: 3, modeOrder: ['hardpoint', 'snd', 'overload'], winsNeeded: 2 };

export function ResultEntryForm({
  matchId,
  compId,
  clan1,
  clan2,
  resolvedSeries,
  games,
  onSaved,
  onCancel,
}: {
  matchId: string;
  compId: string;
  clan1: string;
  clan2: string;
  resolvedSeries?: { length: number; modeOrder: string[]; winsNeeded: number };
  // A CONFIRMED match's already-recorded per-game scores (MatchSummary.games,
  // arc 3 shape) — when present, prefill each game row from it instead of
  // opening blank, so correcting one game (the OCR-fix flow) doesn't force
  // the admin to retype every other game too. Games beyond what's recorded
  // (or when this is unset, e.g. a never-scored match) stay blank/'0', same
  // as the original empty-form behavior.
  games?: MatchCardGameResult[];
  onSaved: () => void;
  onCancel: () => void;
}) {
  const series = resolvedSeries ?? FALLBACK_SERIES;
  const [scores, setScores] = useState<Array<{ c1: string; c2: string }>>(
    () => series.modeOrder.map((_, i) => {
      const g = games?.[i];
      return g ? { c1: String(g.clan1Score), c2: String(g.clan2Score) } : { c1: '', c2: '' };
    }),
  );
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: (games: Array<{ modeKey?: string; clan1Score: number; clan2Score: number }>) =>
      api.enterMatchResult(matchId, compId, games),
    onSuccess: () => { setError(''); onSaved(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const setScore = (i: number, side: 'c1' | 'c2', value: string) => {
    setScores(prev => prev.map((s, si) => (si === i ? { ...s, [side]: value } : s)));
  };

  // Only games with BOTH scores filled, contiguous from the start of the
  // series, count towards the running tally / get submitted — an admin can
  // leave trailing games blank once the series is already decided.
  const firstGap = scores.findIndex(s => s.c1.trim() === '' || s.c2.trim() === '');
  const filledCount = firstGap === -1 ? scores.length : firstGap;

  let c1Wins = 0;
  let c2Wins = 0;
  for (let i = 0; i < filledCount; i++) {
    if (Number(scores[i].c1) > Number(scores[i].c2)) c1Wins++; else c2Wins++;
  }
  const decided = Math.max(c1Wins, c2Wins) >= series.winsNeeded;

  const handleSave = () => {
    if (filledCount === 0) { setError('Enter at least one game.'); return; }
    const games = scores.slice(0, filledCount).map((s, i) => ({
      modeKey: series.modeOrder[i],
      clan1Score: Number(s.c1),
      clan2Score: Number(s.c2),
    }));
    mut.mutate(games);
  };

  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-2">
        {series.modeOrder.map((mode, i) => (
          <div key={i} className="flex items-center gap-2 rounded-lg bg-white/[0.03] px-3 py-2">
            <span className="w-20 shrink-0 font-mono text-[10px] font-bold uppercase tracking-wider text-ink-meta">
              G{i + 1} · {getModeShortLabel(CDL_2026_MODES, mode)}
            </span>
            <input
              type="number"
              min={0}
              inputMode="numeric"
              value={scores[i].c1}
              onChange={e => setScore(i, 'c1', e.target.value)}
              placeholder="0"
              className="input w-16 py-1 text-center text-xs [color-scheme:dark]"
            />
            <span className="text-xs text-ink-meta">–</span>
            <input
              type="number"
              min={0}
              inputMode="numeric"
              value={scores[i].c2}
              onChange={e => setScore(i, 'c2', e.target.value)}
              placeholder="0"
              className="input w-16 py-1 text-center text-xs [color-scheme:dark]"
            />
          </div>
        ))}
      </div>

      <p className="font-mono text-xs text-ink-secondary">
        {clan1} {c1Wins}-{c2Wins} {clan2}
        {decided && <span className="ml-2 font-semibold text-accent-500">— {c1Wins > c2Wins ? clan1 : clan2} wins</span>}
      </p>

      {error && <p className="text-xs text-red-400">{error}</p>}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={mut.isPending || filledCount === 0}
          className="btn-primary flex-1 py-1.5 text-xs disabled:opacity-40"
        >
          {mut.isPending ? 'Saving…' : 'Save result'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg bg-white/[0.05] px-3 py-1.5 text-xs font-semibold text-ink-secondary transition-colors hover:bg-white/[0.08]"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
